/**
 * usePipelineFilterOptions — feeds the Pipeline PropertyFilter's candidate values
 * from the backend `GetPipelineFilterOptions` op (CR2, design §4.4/§4.5).
 *
 * Two field behaviors on ONE op:
 *  - SFDC_STAGE: whole 7-bucket set in one batch (searchTerm/paging ignored),
 *    fetched once. {value: bucket key, label: display name}.
 *  - ACCOUNT_NAME / OPP_NAME / OPP_ID / OPP_OWNER: ILIKE substring search + keyset
 *    paging, driven by Cloudscape `onLoadItems` (type/scroll). value == label.
 *
 * Three Cloudscape rules baked in (each a real bug if dropped — see design §5.3):
 *  1. debounce ~300ms + race-guard: onLoadItems fires on every keystroke (Cloudscape
 *     has no built-in debounce); we debounce and only apply the latest request's
 *     response (drop stale) so a slower earlier request can't overwrite a newer one.
 *  2. ACCUMULATE options, never replace: a selected value's option must stay present
 *     across searches, else the multiselect union-on-toggle silently drops prior
 *     selections. We merge each batch into the field's option set (dedup by value).
 *  3. options are scoped by the current IVT filters (industries = permission scope,
 *     §4.6) — the caller passes them so out-of-permission identity values don't leak.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PropertyFilterProps } from '@amzn/awsui-components-react';
import {
  getIvtClient,
  GetPipelineFilterOptionsCommand,
  SfdcFilterField,
  type GetPipelineFilterOptionsCommandInput,
} from '../../client/IvtHubClient';

/** The IVT-concept filters that scope options (permission/universe narrowing). */
export interface IvtFilterScope {
  industries?: string[];
  useCases?: string[];
  subTopicIds?: number[];
  assetIds?: string[];
  motionIds?: string[];
  hasMotion?: boolean;
}

/** Which fields are searched via onLoadItems (the identity fields; everything else is
 *  a fixed whole-set field — see FIXED_SET_FIELDS — fetched once, not ILIKE-searched). */
const SEARCHABLE_FIELDS: ReadonlySet<string> = new Set([
  SfdcFilterField.ACCOUNT_NAME,
  SfdcFilterField.OPP_NAME,
  SfdcFilterField.OPP_ID,
  SfdcFilterField.OPP_OWNER,
]);

/** Fixed whole-set fields: the backend returns the entire candidate set in one batch
 *  (searchTerm/paging ignored). Prefetched on scope change; NOT in SEARCHABLE_FIELDS.
 *  SFDC_STAGE = 7 buckets; PIPELINE_SCORE = the distinct scores that occur (data-driven,
 *  whole-universe — NOT IVT-scoped, so a score can't vanish when an industry is picked). */
const FIXED_SET_FIELDS: readonly string[] = [SfdcFilterField.SFDC_STAGE, SfdcFilterField.PIPELINE_SCORE];

const DEBOUNCE_MS = 300;
const PAGE_SIZE = 50;

type Option = PropertyFilterProps.FilteringOption;

/** Merge a fresh batch into the existing list, dedup by (propertyKey, value),
 *  preserving existing order then appending new ones (accumulate, never replace). */
function mergeOptions(prev: Option[], batch: Option[]): Option[] {
  if (batch.length === 0) return prev;
  const seen = new Set(prev.map((o) => `${o.propertyKey}${o.value}`));
  const added = batch.filter((o) => !seen.has(`${o.propertyKey}${o.value}`));
  return added.length ? [...prev, ...added] : prev;
}

function scopeToInput(scope: IvtFilterScope): Partial<GetPipelineFilterOptionsCommandInput> {
  return {
    industries: scope.industries?.length ? scope.industries : undefined,
    useCases: scope.useCases?.length ? scope.useCases : undefined,
    subTopicIds: scope.subTopicIds?.length ? scope.subTopicIds : undefined,
    assetIds: scope.assetIds?.length ? scope.assetIds : undefined,
    motionIds: scope.motionIds?.length ? scope.motionIds : undefined,
    hasMotion: scope.hasMotion,
  };
}

export interface PipelineFilterOptionsState {
  /** Flat option list for PropertyFilter's `filteringOptions` (all fields). */
  filteringOptions: Option[];
  /** PropertyFilter's `filteringStatusType` (loading / finished / error). */
  status: PropertyFilterProps.StatusType;
  /** Wire to PropertyFilter's `onLoadItems`. */
  onLoadItems: (e: { detail: PropertyFilterProps.LoadItemsDetail }) => void;
}

/**
 * @param scope current IVT-concept filters (options are scoped to these).
 * @param enabled gate loading until permission resolves (same race as the list).
 */
export function usePipelineFilterOptions(
  scope: IvtFilterScope,
  enabled: boolean,
): PipelineFilterOptionsState {
  const [options, setOptions] = useState<Option[]>([]);
  const [status, setStatus] = useState<PropertyFilterProps.StatusType>('pending');

  // Keyset cursor per field (for scroll paging). Reset when the search term or
  // scope changes (firstPage).
  const tokensRef = useRef<Record<string, string | undefined>>({});
  // Race guard — PER FIELD: a field's response is only superseded by a NEWER request
  // for the SAME field. A global counter was a bug: an identity search (Account)
  // would invalidate the still-in-flight Stage prefetch, leaving Stage permanently
  // empty (Stage's response arrives with a stale global seq → dropped).
  const seqByFieldRef = useRef<Record<string, number>>({});
  // Which field's request is the latest overall — drives `status` so the displayed
  // loading/finished state belongs to the field the user is currently interacting
  // with (a Stage prefetch resolving must not flip an open Account dropdown to
  // "finished"). Also gates the setStatus at request time.
  const activeFieldRef = useRef<string | undefined>(undefined);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Serialize the scope so a change invalidates in-flight loads + cursors + options.
  const scopeKey = JSON.stringify(scopeToInput(scope));

  const fetchBatch = useCallback(
    // `background` = a fixed-set PREFETCH (Stage/Score on scope change), NOT a user
    // interaction. A background fetch must NOT claim activeFieldRef: with two fixed-set
    // prefetches, the last-started one would otherwise steal it and gate out the other's
    // status — e.g. a failed Stage prefetch would be masked by a finished Score prefetch,
    // leaving the Stage dropdown silently empty with no error/Retry. Instead a background
    // fetch reflects status only while NO field is being interacted with, and its success
    // never downgrades a sibling's 'error' (error wins so any fixed-set failure is visible).
    async (field: string, searchTerm: string, firstPage: boolean, background = false) => {
      if (!enabled) return;
      const seq = (seqByFieldRef.current[field] = (seqByFieldRef.current[field] ?? 0) + 1);
      if (!background) activeFieldRef.current = field;
      // A field "owns" the status if it's the actively-interacted field (foreground) or,
      // for a background prefetch, if nothing is being interacted with right now.
      const ownsStatus = () => (background ? activeFieldRef.current === undefined : activeFieldRef.current === field);
      if (ownsStatus()) setStatus('loading');
      const token = firstPage ? undefined : tokensRef.current[field];
      try {
        const out = await getIvtClient().send(
          new GetPipelineFilterOptionsCommand({
            field: field as SfdcFilterField,
            searchTerm: searchTerm || undefined,
            ...scopeToInput(scope),
            nextToken: token,
            maxResults: PAGE_SIZE,
          }),
        );
        // Per-field race guard: drop if a newer request for THIS field superseded it.
        if (seq !== seqByFieldRef.current[field]) return;
        const batch: Option[] = (out.items ?? [])
          .filter((o) => !!o.value)
          .map((o) => ({
            propertyKey: field,
            value: o.value as string,
            // PIPELINE_SCORE: the backend doesn't own the score rubric, so it returns
            // the bare number ("3"); the frontend formats the display label ("3分").
            // The token VALUE stays the bare number (that's what the tree sends back).
            label: field === SfdcFilterField.PIPELINE_SCORE ? `${o.value}分` : o.label ?? o.value,
          }));
        tokensRef.current[field] = out.nextToken;
        setOptions((prev) => mergeOptions(prev, batch));
        if (ownsStatus()) {
          const next = out.nextToken ? 'pending' : 'finished';
          // A background success must not clobber a sibling prefetch's 'error' (both
          // fixed-set fields share one control status; error wins so a failure shows).
          setStatus((prev) => (background && prev === 'error' ? 'error' : next));
        }
      } catch {
        if (seq !== seqByFieldRef.current[field]) return;
        if (ownsStatus()) setStatus('error');
      }
    },
    [enabled, scope, scopeKey], // scopeKey drives re-creation on scope change
  );

  // On scope change (or first enable): the IVT-filter universe changed, so every
  // field's candidates are now different. CLEAR the accumulated options + per-field
  // cursors and re-prefetch Stage. (Accumulate-never-replace applies WITHIN a scope
  // — across a scope change we must start fresh, else stale options from the old
  // industry linger; that was the "industry change doesn't narrow the dropdown" bug.)
  // Identity fields re-load lazily when their dropdown next fires onLoadItems.
  useEffect(() => {
    // Cancel any pending debounce timer FIRST: it captured the OLD-scope fetchBatch
    // closure; if it fired after the seq-bump below it would run a fresh fetch (new
    // seq → passes the per-field guard) and apply OLD-scope options — a §4.6 scope
    // violation (e.g. leaking GAME accounts after switching to FSI). The seq-bump only
    // guards requests ALREADY in flight, not a timer that fires post-bump.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!enabled) {
      setOptions([]);
      return;
    }
    setOptions([]);
    tokensRef.current = {};
    // Bump every field's seq so any old-scope request still in flight is dropped when
    // it resolves (its captured seq no longer matches) — a fetch that outlives the
    // scope change can't append old-scope rows / flip status.
    for (const k of Object.keys(seqByFieldRef.current)) seqByFieldRef.current[k]++;
    activeFieldRef.current = undefined;
    // Prefetch every fixed whole-set field (Stage + Score) as BACKGROUND fetches: they
    // don't claim activeFieldRef, so neither one's status masks the other's (a failed
    // Stage prefetch stays visible even though Score prefetch finishes). PIPELINE_SCORE
    // is whole-universe (scope-independent), so re-fetching it on scope change returns
    // the same set — harmless, and keeps the fixed-set handling uniform.
    for (const f of FIXED_SET_FIELDS) void fetchBatch(f, '', true, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, scopeKey]);

  const onLoadItems = useCallback(
    (e: { detail: PropertyFilterProps.LoadItemsDetail }) => {
      const field = e.detail.filteringProperty?.key;
      if (!field || !SEARCHABLE_FIELDS.has(field)) return; // Stage / free-text → ignore
      const searchTerm = e.detail.filteringText ?? '';
      const firstPage = e.detail.firstPage !== false; // firstPage true unless explicitly a "load more"
      if (firstPage) {
        // New search term → reset this field's cursor + debounce the request.
        tokensRef.current[field] = undefined;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          void fetchBatch(field, searchTerm, true);
        }, DEBOUNCE_MS);
      } else {
        // Scroll-to-bottom "load more" — no debounce, fetch the next page now.
        void fetchBatch(field, searchTerm, false);
      }
    },
    [fetchBatch],
  );

  // Clean up a pending debounce on unmount.
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  return { filteringOptions: options, status, onLoadItems };
}
